import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { LocationQuery } from 'vue-router'
import Network from '@/Network'
import { ServiceError, getApiErrorMessage } from '@/utils/apiError'
import type { DashboardSavedView } from '@/Network/Types/Responses/DashboardSavedViewResponses'
import type { DashboardSavedViewFilters } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import {
  DEFAULT_LENS_STATE,
  lensToViewState,
  parseLensQuery,
  viewStateToLens,
  viewStatesEqual,
  type LensState
} from '@/modules/company/Dashboard/lenses/lensState'

export const SAVED_VIEWS_LOAD_ERROR = 'Não foi possível carregar as visões salvas'

/** Visões são por usuário e empresa (spec §3.5): usa a empresa única selecionada ou a primeira do usuário. */
export function resolveViewsCompanyId(
  selected: Set<number>,
  myCompanies: { id: number }[]
): number | null {
  if (selected.size === 1) return [...selected][0]
  return myCompanies[0]?.id ?? null
}

export const useDashboardLensStore = defineStore('dashboardLens', () => {
  const lens = ref<LensState>({ ...DEFAULT_LENS_STATE })
  const views = ref<DashboardSavedView[]>([])
  const activeViewId = ref<number | null>(null)
  const viewsCompanyId = ref<number | null>(null)
  const viewsLoadError = ref<string | null>(null)
  const isSavingView = ref(false)

  const activeView = computed(() => views.value.find((v) => v.id === activeViewId.value) ?? null)
  const defaultView = computed(() => views.value.find((v) => v.isDefault) ?? null)

  function hydrateFromQuery(query: LocationQuery): void {
    lens.value = parseLensQuery(query)
  }

  function setLens(patch: Partial<LensState>): void {
    lens.value = { ...lens.value, ...patch }
  }

  function resetLens(): void {
    lens.value = { ...DEFAULT_LENS_STATE }
  }

  function isActiveViewDirty(filters: DashboardSavedViewFilters): boolean {
    if (!activeView.value) return false
    return !viewStatesEqual(activeView.value.state, lensToViewState(lens.value, filters))
  }

  async function fetchViews(companyId: number): Promise<void> {
    viewsCompanyId.value = companyId
    try {
      viewsLoadError.value = null
      const list = await Network.dashboardSavedView.list(companyId)
      // Empresa trocada no meio: a resposta é de outra empresa.
      if (viewsCompanyId.value !== companyId) return
      views.value = list
    } catch (error) {
      if (viewsCompanyId.value !== companyId) return
      // fallback intencional: sem visões a tela funciona igual; o chip mostra o erro (viewsLoadError).
      views.value = []
      viewsLoadError.value = getApiErrorMessage(error, SAVED_VIEWS_LOAD_ERROR)
    }
  }

  function markDefault(id: number, isDefault: boolean): void {
    views.value = views.value.map((v) => ({
      ...v,
      isDefault: v.id === id ? isDefault : isDefault ? false : v.isDefault
    }))
  }

  async function saveView(input: {
    name: string
    isDefault: boolean
    filters: DashboardSavedViewFilters
  }): Promise<DashboardSavedView> {
    if (viewsCompanyId.value === null) throw new ServiceError('Não há empresa para salvar a visão')
    isSavingView.value = true
    try {
      const created = await Network.dashboardSavedView.create({
        companyId: viewsCompanyId.value,
        name: input.name.trim(),
        isDefault: input.isDefault,
        state: lensToViewState(lens.value, input.filters)
      })
      views.value = [...views.value, created]
      if (created.isDefault) markDefault(created.id, true)
      activeViewId.value = created.id
      return created
    } finally {
      isSavingView.value = false
    }
  }

  async function updateActiveView(filters: DashboardSavedViewFilters): Promise<void> {
    // Em voo: um 2º clique em "atualizar" não repete o PUT.
    if (!activeView.value || isSavingView.value) return
    isSavingView.value = true
    try {
      const updated = await Network.dashboardSavedView.update(activeView.value.id, {
        state: lensToViewState(lens.value, filters)
      })
      views.value = views.value.map((v) => (v.id === updated.id ? updated : v))
    } finally {
      isSavingView.value = false
    }
  }

  async function setDefaultView(id: number, isDefault: boolean): Promise<void> {
    await Network.dashboardSavedView.update(id, { isDefault })
    markDefault(id, isDefault)
  }

  /** Renomear no celular (spec R8.5): mesmo `PUT` da visão, só com o nome. */
  async function renameView(id: number, name: string): Promise<void> {
    const trimmed = name.trim()
    if (!trimmed) throw new ServiceError('Informe um nome para a visão')
    const updated = await Network.dashboardSavedView.update(id, { name: trimmed })
    views.value = views.value.map((v) => (v.id === updated.id ? updated : v))
  }

  async function deleteView(id: number): Promise<void> {
    await Network.dashboardSavedView.remove(id)
    views.value = views.value.filter((v) => v.id !== id)
    if (activeViewId.value === id) activeViewId.value = null
  }

  function applyView(view: DashboardSavedView): DashboardSavedViewFilters {
    lens.value = viewStateToLens(view.state)
    activeViewId.value = view.id
    return { ...view.state.filters }
  }

  function $reset(): void {
    lens.value = { ...DEFAULT_LENS_STATE }
    views.value = []
    activeViewId.value = null
    viewsCompanyId.value = null
    viewsLoadError.value = null
    isSavingView.value = false
  }

  return {
    lens,
    views,
    activeViewId,
    activeView,
    defaultView,
    viewsCompanyId,
    viewsLoadError,
    isSavingView,
    hydrateFromQuery,
    setLens,
    resetLens,
    isActiveViewDirty,
    fetchViews,
    saveView,
    updateActiveView,
    setDefaultView,
    renameView,
    deleteView,
    applyView,
    $reset
  }
})
